/* eslint-disable */
import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import axios from 'axios';
import DataTable from 'react-data-table-component';
import ReactToPrint from 'react-to-print';

import AnswerTable from './Components/AnswerTable';
import WritingSampleAnswers, {
  POST_TEST_WRITING_SAMPLES,
  PRE_TEST_WRITING_SAMPLES
} from './Components/WritingSampleAnswers';
import { OtherColor, PrimaryColor, SecondaryColor, WhiteColor } from '../../Assets/colorPalette';
import { sanitizePrintDocumentTitle } from '../../Assets/utils';
import {
  listeningAnswerKey as listeningAnswerKeyPreTest,
  readingAnswerKey as readingAnswerKeyPreTest
} from './Components/PreTestComponents/AnswerKey';
import {
  listeningAnswerKey as listeningAnswerKeyPostTest,
  readingAnswerKey as readingAnswerKeyPostTest
} from './Components/PostTestComponents/AnswerKey';

const TEST_TYPE = {
  PRE_TEST: 'Pre Test',
  POST_TEST: 'Post Test'
};

const _renderTableContent = (selected, listeningAnswer, readingAnswer, writingAnswerArray, testType) => {
  const getListeningAnswerKey =
    testType === TEST_TYPE.PRE_TEST ? listeningAnswerKeyPreTest : listeningAnswerKeyPostTest;
  const getReadingAnswerKey =
    testType === TEST_TYPE.PRE_TEST ? readingAnswerKeyPreTest : readingAnswerKeyPostTest;
  const writingSamples =
    testType === TEST_TYPE.PRE_TEST ? PRE_TEST_WRITING_SAMPLES : POST_TEST_WRITING_SAMPLES;

  return (
    <>
      <Row>
        <Col md={4} sm={4}>
          <b>Listening Score</b>
        </Col>
        <Col md={1} sm={1}>
          :
        </Col>
        <Col>{selected?.listening}</Col>
      </Row>
      <Row className="mt-2">
        <Col md={4} sm={4}>
          <b>Reading Score</b>
        </Col>
        <Col md={1} sm={1}>
          :
        </Col>
        <Col>{selected?.reading}</Col>
      </Row>
      <hr />
      <AnswerTable
        listeningAnswer={listeningAnswer}
        listeningAnswerKey={getListeningAnswerKey}
        readingAnswer={readingAnswer}
        readingAnswerKey={getReadingAnswerKey}
        writingAnswer={writingAnswerArray}
      />
      <hr className="mt-4" />
      <WritingSampleAnswers samples={writingSamples} />
    </>
  );
};

const ScoreView = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [testFilter, setTestFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [show, setShow] = useState(false);

  const [listeningAnswer, setListeningAnswer] = useState([]);
  const [readingAnswer, setReadingAnswer] = useState([]);

  const [listeningAnswerArray, setListeningAnswerArray] = useState([]);
  const [readingAnswerArray, setReadingAnswerArray] = useState([]);
  const [writingAnswerArray, setWritingAnswerArray] = useState([]);

  const [isDataReadyForPrint, setIsDataReadyForPrint] = useState(false);

  const [testType, setTestType] = useState(TEST_TYPE.PRE_TEST);

  useEffect(() => {
    // This useEffect hook will be used to update the ReactToPrint component when the data is ready for print.
    if (isDataReadyForPrint && componentRef.current) {
      componentRef.current.handlePrint();
      setIsDataReadyForPrint(false);
    }
  }, [isDataReadyForPrint]);

  const componentRef = useRef();

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true);

  const fetchData = async (pageNum, limit, searchQuery = '', testQuery = '') => {
    setLoading(true);
    try {
      const params = { page: pageNum, limit };
      if (searchQuery) {
        params.search = searchQuery;
      }
      if (testQuery) {
        params.test = testQuery;
      }

      const {
        data: {
          data: { scoreView, total }
        }
      } = await axios.get('https://api.inggo.id/inggo', { params });
      setData(scoreView);
      setTotalRows(total);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  const deleteData = async (id) => {
    try {
      await axios.delete('https://api.inggo.id/inggo/' + id);
      const wasOnlyRowOnPage = data.length === 1 && page > 1;
      const nextPage = wasOnlyRowOnPage ? page - 1 : page;
      setPage(nextPage);
      await fetchData(nextPage, perPage, search, testFilter);
    } catch (e) {
      console.log(e);
    }
  };

  const convertTimeStamp = (time) => {
    return new Date(time).toISOString().slice(0, 19).replace('T', ' ');
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchData(page, perPage, search, testFilter);
  }, [page, perPage, search, testFilter]);

  const formattedData = (item) => {
    const formatData = JSON.parse(item?.answer);
    const { listeningAnswer, readingAnswer } = formatData;

    const writingAnswer = [item?.firstWriting, item?.secondWriting];

    setListeningAnswer(listeningAnswer);
    setReadingAnswer(readingAnswer);

    let tempListeningArray = [];
    let tempReadingArray = [];
    for (let i = 1; i <= 40; i++) {
      tempListeningArray.push(listeningAnswer[i]);
      tempReadingArray.push(readingAnswer[i]);
    }

    setListeningAnswerArray(tempListeningArray);
    setReadingAnswerArray(tempReadingArray);
    setWritingAnswerArray(writingAnswer);
  };

  const ComponentToPrint = React.forwardRef((props, ref) => {
    return (
      <div ref={ref} className="p-5">
        <h4 className="text-center mb-4">
          <b>
            {selected?.name} ({selected?.test})
          </b>
        </h4>
        {_renderTableContent(selected, listeningAnswer, readingAnswer, writingAnswerArray, testType)}
      </div>
    );
  });

  const columns = [
    {
      name: 'Name',
      selector: (row) => row.name,
      sortable: true,
      wrap: true,
      grow: 2,
      minWidth: '220px',
      style: {
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'unset'
      }
    },
    {
      name: 'Email',
      selector: (row) => row.email,
      sortable: true,
      wrap: true,
      grow: 2,
      minWidth: '200px',
      style: {
        whiteSpace: 'normal',
        overflow: 'visible',
        textOverflow: 'unset',
        wordBreak: 'break-word'
      },
      cell: (row) => <>{row.email}</>
    },
    {
      name: 'Phone Number',
      selector: (row) => row.notelp,
      sortable: true,
      minWidth: '140px'
    },
    {
      name: 'Test',
      selector: (row) => row.test,
      sortable: true,
      cell: (row) => (
        <>
          {row?.test === 'Pre Test' ? (
            <span className="btn btn-sm btn-info text-light">{row?.test}</span>
          ) : (
            <span className="btn btn-sm btn-primary">{row?.test}</span>
          )}
        </>
      )
    },
    {
      name: 'Listening',
      selector: (row) => row.listening,
      sortable: true,
    },
    {
      name: 'Reading',
      selector: (row) => row.reading,
      sortable: true,
    },
    {
      name: 'Time',
      selector: (row) => row.createdAt,
      sortable: true,
      cell: (row) => convertTimeStamp(row.createdAt)
    },
    {
      name: '#',
      cell: (row) => (
        <>
          <i
            className="bi bi-eye"
            onClick={() => {
              setTestType(row.test);
              setSelected(row);
              formattedData(row);
              handleShow();
            }}
            style={{
              color: WhiteColor,
              backgroundColor: PrimaryColor,
              padding: 5,
              borderRadius: '8px',
              cursor: 'pointer',
              marginRight: '2px'
            }}
          />
          <ReactToPrint
            trigger={() => (
              <i
                className="bi bi-download"
                style={{
                  color: WhiteColor,
                  backgroundColor: OtherColor,
                  padding: 5,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  marginRight: '2px'
                }}
              />
            )}
            onBeforeGetContent={() => {
              return new Promise((resolve) => {
                setSelected(row);
                setTestType(row.test);
                formattedData(row);
                resolve();
              });
            }}
            content={() => componentRef.current}
            documentTitle={sanitizePrintDocumentTitle(row.name, row.test)}
          />
          <i
            className="bi bi-trash"
            onClick={() => {
              window.confirm('Are you sure you wish to delete this item?')
                ? deleteData(row?.id)
                : () => {
                    console.log('');
                  };
            }}
            style={{
              color: WhiteColor,
              backgroundColor: SecondaryColor,
              padding: 5,
              borderRadius: '8px',
              cursor: 'pointer'
            }}
          />
        </>
      )
    }
  ];

  return (
    <>
      <div className="p-5" style={{ minHeight: '75vh' }}>
        <Row className="mb-3">
          <Col md={6} lg={4}>
            <Form.Control
              type="search"
              placeholder="Search by name, email, or phone number..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </Col>
          <Col md={4} lg={3} className="mt-2 mt-md-0">
            <Form.Select
              aria-label="Filter by test type"
              value={testFilter}
              onChange={(e) => {
                setTestFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All tests</option>
              <option value={TEST_TYPE.PRE_TEST}>{TEST_TYPE.PRE_TEST}</option>
              <option value={TEST_TYPE.POST_TEST}>{TEST_TYPE.POST_TEST}</option>
            </Form.Select>
          </Col>
        </Row>
        <DataTable
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          paginationDefaultPage={page}
          paginationPerPage={perPage}
          progressPending={loading}
          onChangePage={(newPage) => {
            setPage(newPage);
          }}
          onChangeRowsPerPage={(newPerPage, newPage) => {
            setPerPage(newPerPage);
            setPage(newPage);
          }}
        />
      </div>
      <div style={{ display: 'none' }}>
        <ComponentToPrint ref={componentRef} />
      </div>
      <Modal show={show} onHide={handleClose} animation={false} size="lg" fullscreen>
        <Modal.Header closeButton>
          <Modal.Title>
            {selected?.name} ({selected?.test})
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selected &&
            _renderTableContent(
              selected,
              listeningAnswer,
              readingAnswer,
              writingAnswerArray,
              testType
            )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ScoreView;
